import { ErrorBoundary } from '@vp/swan'
import useClient from '../../../hooks/useClient'
import LinkWithIcon from './LinkWithIcon'
import { MyAccountResourcesContentProvider, useMyAccountResourcesContent } from '@vp/my-account-pages-dropdown'
import { useMemo } from 'react'
import { useI18n } from '../../../context/TranslationContext'
import { DEFAULT_MY_FAVORITES_URL } from '../../../constants/my-account'
import { toSWANIconType } from '../../../utils/swan-icon-converter'

/**
 * SSR-safe wrapper for the Favorites component.
 * Returns a placeholder during SSR to avoid useAuth hook which requires IdentityProvider.
 */
const FavoritesHeader: React.FC = () => {
  const isClient = useClient()
  const { translate } = useI18n()
  const ssrFavoritesComponent = <LinkWithIcon href={DEFAULT_MY_FAVORITES_URL} iconType="heart" label={translate('myFavorites')} />

  // Return placeholder during SSR to avoid useAuth call which requires IdentityProvider
  if (!isClient) return ssrFavoritesComponent

  return (
    <ErrorBoundary fallback={ssrFavoritesComponent}>
      <MyAccountResourcesContentProvider>
        <FavoritesHeaderClient />
      </MyAccountResourcesContentProvider>
    </ErrorBoundary>
  )
}

/**
 * Client-side only component that renders the Favorites header.
 * Separated to ensure useMyAccountResourcesContent is only called on the client.
 */
const FavoritesHeaderClient: React.FC = () => {
  const { translate } = useI18n()
  const { content } = useMyAccountResourcesContent()
  const myFavoritesLink = useMemo(() => content?.signedIn.bookendsHeaderLinks.find((link) => link.id === 'myFavorites'), [content])

  return (
    <LinkWithIcon
      href={myFavoritesLink?.url ?? DEFAULT_MY_FAVORITES_URL}
      iconType={toSWANIconType(myFavoritesLink?.iconType, 'heart')}
      label={myFavoritesLink?.text ?? translate('myFavorites')}
      analyticsId="myFavorites"
    />
  )
}

export default FavoritesHeader
